import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { useNavigation, NavigationProp } from "@react-navigation/native";
import { PaletteIcon, XIcon } from "@phosphor-icons/react";

import type { RootStackParamList } from "../navigation/navigation.types";
import CompanionLayout from "../layouts/CompanionLayout";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import { useCompanionThreads } from "../contexts/CompanionThreadsContext";
import {
  Todo as GraphqlTodo,
  useRecentTodosQuery,
  useGetAllCompanionChatsForUserQuery,
  useGetAssignmentsWithCourseQuery,
  AttachmentType,
} from "../graphql/generated/graphql";
import UnicornStudio from "unicornstudio-react";
import { useAppConfig } from "../AppConfigProvider";

// Bundled as static asset so /static/media/... is served as JSON (avoids SPA fallback returning HTML)
const auroraChatSceneJsonUrl = require("../web/unicorn-aurora-chat.json") as string;
import { colors, radii, semanticColors, spacing, typographyScale } from "../styles/design-system";
import { RecentChat } from "../components/chat-companion/SummaryCard/RecentChat";
import { UpcomingTodos } from "../components/chat-companion/SummaryCard/UpcomingTodos";
import { UpcomingAssignments } from "../components/chat-companion/SummaryCard/UpcomingAssignments";
import ChatInputController from "./../containers/companion/ChatInputController";
import type { Assignment } from "../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import type { Todo as TaskModalTodo } from "../components/chatNewInterface/interfaceComponents/modals/AddTaskModal";
import type { AttachmentInputType } from "../containers/companion/types";
import type { ToolId } from "../components/chatNewInterface/interfaceComponents/ToolsMenu/toolOptions";
import { GENERAL_WORKSPACE_ID } from "../hooks/useWorkspaceLists";
import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAuth } from "../hooks/useAuth";
import { MathVirtualKeyboardProvider } from "../contexts/MathVirtualKeyboardContext";
import CompanionOnboardingContainer from "../containers/companion/CompanionOnboardingContainer";

function CompanionHomeView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { isLarge } = useResponsiveScreen();
  const { meData, loadingMe } = useAuth();
  const { showError } = useCompanionThreads();
  const { width } = useWindowDimensions();
  const isMedium = width >= 768;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const [preselectedAssignment, setPreselectedAssignment] = useState<Assignment | null>(null);
  const [preselectedTodo, setPreselectedTodo] = useState<TaskModalTodo | null>(null);
  const [dismissedOnboardingPrompt, setDismissedOnboardingPrompt] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [auroraSceneError, setAuroraSceneError] = useState(false);
  const handleAuroraError = useCallback(() => setAuroraSceneError(true), []);

  // Check if user has completed companion onboarding
  const hasCompletedOnboarding = useMemo(() => {
    const isOnboarded = meData?.me?.isOnboarded || false;
    return isOnboarded;
  }, [meData?.me?.isOnboarded]);

  const handleOnboardingPromptDismiss = useCallback(() => {
    setDismissedOnboardingPrompt(true);
  }, []);

  const handleStartOnboarding = useCallback(() => {
    // Navigate to ChatSettings view with AI Companion tab selected
    navigation.navigate("Chat Settings", { initialTab: "aiCompanion" });
  }, [navigation]);

  const handleOnboardingModalClose = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  // Automatically open onboarding modal on load if user hasn't completed it
  useEffect(() => {
    if (!loadingMe && !hasCompletedOnboarding) {
      setShowOnboarding(true);
    }
  }, [loadingMe, hasCompletedOnboarding]);

  // Reset onboarding state when user completes onboarding
  useEffect(() => {
    if (hasCompletedOnboarding) {
      setDismissedOnboardingPrompt(false);
    }
  }, [hasCompletedOnboarding]);

  const { data: recentTodosData, loading: recentTodosLoading, refetch: refetchTodos } = useRecentTodosQuery({});
  const recentTodosResult = recentTodosData?.getRecentTodosForUser;
  const recentTodosSuccess = recentTodosResult?.success !== false;
  const { data: chatsData } = useGetAllCompanionChatsForUserQuery({});
  const courseWorkspaces = chatsData?.getAllCompanionChatsForUser?.data?.courseWorkspaces ?? [];

  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];
  const { data: assignmentsData } = useGetAssignmentsWithCourseQuery({
    variables: {
      schoolId,
    },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });
  const upcomingAssignments = assignmentsData?.getAssignmentsWithCourse?.data ?? [];

  const todos = useMemo(
    () => recentTodosResult?.data?.filter((todo): todo is GraphqlTodo => Boolean(todo)) ?? [],
    [recentTodosResult]
  );

  const findWorkspaceIdFromCourseId = useCallback(
    (courseId: string | undefined): string | null => {
      if (!courseId) {
        return null;
      }

      // Course workspace keys are in the format: ${userId}|${courseId}
      const workspace = courseWorkspaces.find((ws) => {
        if (!ws.key || typeof ws.key !== "string") {
          return false;
        }
        const parts = ws.key.split("|");
        return parts.length === 2 && parts[1] === courseId;
      });

      return workspace?.id ?? null;
    },
    [courseWorkspaces]
  );

  const handleSelectTodo = useCallback((todo: GraphqlTodo) => {
    setPreselectedAssignment(null);
    setPreselectedTodo({
      id: todo.id,
      name: todo.name,
      description: todo.description ?? undefined,
      date: todo.date ?? undefined,
      completed: todo.completed ?? undefined,
    });
  }, []);

  const handleSelectAssignment = useCallback((assignment: Assignment) => {
    setPreselectedTodo(null);
    setPreselectedAssignment(assignment);
  }, []);

  const handleSubmit = useCallback(
    (
      message: string,
      attachment: AttachmentInputType | null,
      options?: { workspaceIdOverride?: string | null; activeTool?: ToolId | null }
    ) => {
      if (!message.trim()) {
        return;
      }

      const trimmedInput = message.trim();

      let workspaceId = GENERAL_WORKSPACE_ID;
      let assignmentCourseId: string | undefined = undefined;

      // First try preselectedAssignment
      if (preselectedAssignment?.courseId) {
        assignmentCourseId = preselectedAssignment.courseId;
      }
      // If not found, try to find assignment from attachment ID
      else if (attachment?.type === AttachmentType.Assignment && attachment.id) {
        const assignment = upcomingAssignments.find((a) => a.id === attachment.id);
        if (assignment?.courseId) {
          assignmentCourseId = assignment.courseId;
        }
      }

      if (assignmentCourseId) {
        const courseWorkspaceId = findWorkspaceIdFromCourseId(assignmentCourseId);
        if (courseWorkspaceId) {
          workspaceId = courseWorkspaceId;
        } else {
          console.warn(
            "[CompanionHomeView] Could not find workspace for courseId:",
            assignmentCourseId,
            "Available workspaces:",
            courseWorkspaces.map((ws) => ({ id: ws.id, key: ws.key }))
          );
        }
      }

      setPreselectedAssignment(null);
      setPreselectedTodo(null);

      navigation.navigate("Companion Chat", {
        chatId: undefined,
        initialMessage: {
          text: trimmedInput,
          attachment: attachment ?? undefined,
          workspaceId,
          activeTool: options?.activeTool ?? null,
        },
      });
    },
    [navigation, findWorkspaceIdFromCourseId, preselectedAssignment, courseWorkspaces, upcomingAssignments]
  );

  return (
    <CompanionLayout>
      {!isLarge && <WorkspaceHeader title={"Home"} />}
      <MathVirtualKeyboardProvider>
        <View style={styles.container}>
          <View style={styles.auroraLayer} pointerEvents="none">
            {!auroraSceneError && (
              <UnicornStudio
                jsonFilePath={auroraChatSceneJsonUrl}
                width="100%"
                height="100%"
                scale={1}
                dpi={1}
                lazyLoad={false}
                onError={handleAuroraError}
              />
            )}
          </View>
          <ScrollView contentContainerStyle={[styles.scrollContent, isMedium && styles.scrollContentMd]}>
            <View style={styles.content}>
              <View style={[styles.hero, isMedium && styles.heroMd]}>
                <CustomText
                  style={[styles.headerText, isMedium ? typographyScale.displayLg : typographyScale.displaySm]}
                  text={`${getGreeting()}, ${meData?.me?.name || "there"}`}
                  useTranslationText={false}
                />
                <CustomText
                  style={[
                    styles.subHeaderText,
                    isMedium ? typographyScale.textMd : typographyScale.textSm,
                    isMedium && styles.subHeaderTextMd,
                  ]}
                  text="What can I help you with today?"
                  useTranslationText={false}
                />
                <View style={styles.chatWrapper}>
                  <ChatInputController
                    onSubmit={handleSubmit}
                    placeholder="Ask anything or attach a file..."
                    activeWorkspaceType={"general"}
                    preselectedAssignment={preselectedAssignment}
                    preselectedTodo={preselectedTodo}
                    showError={showError}
                  />
                </View>
              </View>
              {!hasCompletedOnboarding && !dismissedOnboardingPrompt && (
                <View style={styles.onboardingPromptCard}>
                  <View style={styles.onboardingPromptContent}>
                    <View style={styles.onboardingPromptIcon}>
                      <PaletteIcon size={24} weight="regular" color={colors.blue[600]} />
                    </View>
                    <CustomText
                      text="companion_onboarding_prompt_title"
                      size="m"
                      style={styles.onboardingPromptTitle}
                    />
                  </View>
                  <View style={styles.onboardingPromptActions}>
                    <CustomButton
                      text="buttons.companion_onboarding_prompt_setup"
                      onPress={handleStartOnboarding}
                      styleType="link"
                      size="s"
                      style={styles.onboardingPromptButton}
                    />
                    <Pressable
                      onPress={handleOnboardingPromptDismiss}
                      style={styles.onboardingPromptClose}
                      accessibilityLabel="Dismiss onboarding prompt"
                      accessibilityRole="button">
                      <XIcon size={20} weight="bold" color={colors.grey[500]} />
                    </Pressable>
                  </View>
                </View>
              )}
              <View style={[styles.categoriesWrapper, isMedium && styles.categoriesWrapperMd]}>
                <RecentChat />
                {recentTodosSuccess === false ? (
                  <View style={styles.errorCard}>
                    <CustomText
                      text={recentTodosResult?.message ?? "We were not able to get your todos."}
                      size="m"
                      useTranslationText={false}
                      style={styles.errorMessage}
                    />
                    {recentTodosLoading ? (
                      <ActivityIndicator size="small" color={semanticColors.surface.brand} />
                    ) : (
                      <CustomButton
                        text="Retry"
                        onPress={() => void refetchTodos()}
                        useTranslationText={false}
                        styleType="primary"
                        style={styles.retryButton}
                      />
                    )}
                  </View>
                ) : recentTodosLoading ? (
                  <View style={styles.loadingCard}>
                    <ActivityIndicator size="small" color={semanticColors.surface.brand} />
                  </View>
                ) : (
                  <UpcomingTodos todos={todos} onSelectTodo={handleSelectTodo} />
                )}
                <UpcomingAssignments onSelectAssignment={handleSelectAssignment} />
              </View>
            </View>
          </ScrollView>
        </View>
      </MathVirtualKeyboardProvider>
      <CompanionOnboardingContainer visible={showOnboarding} onClose={handleOnboardingModalClose} />
    </CompanionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
    position: "relative",
    overflow: "hidden",
  },
  auroraLayer: {
    ...StyleSheet.absoluteFillObject,
    pointerEvents: "none",
    zIndex: 0,
    ...(Platform.OS === "web"
      ? ({
          position: "fixed",
          width: "100vw",
          height: "100vh",
        } as unknown as object)
      : {}),
  },
  scrollContent: {
    paddingHorizontal: 12,
    paddingVertical: 24,
  },
  scrollContentMd: {
    paddingHorizontal: 16,
    paddingVertical: 64,
  },
  content: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 1,
  },
  hero: {
    width: "100%",
    maxWidth: 768,
    alignSelf: "center",
    marginBottom: 24,
    gap: 8,
  },
  heroMd: {
    marginBottom: 48,
    gap: 12,
  },
  headerText: {
    textAlign: "center",
    color: semanticColors.typography.displayPrimary,
    fontWeight: 600,
  },
  subHeaderText: {
    textAlign: "center",
    color: colors.grey[600],
    marginBottom: 24,
  },
  subHeaderTextMd: {
    marginBottom: 32,
  },
  chatWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    maxWidth: 768,
    alignSelf: "center",
  },
  categoriesWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    justifyContent: "center",
    alignContent: "center",
    maxWidth: 1024,
    alignSelf: "center",
  },
  categoriesWrapperMd: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "center",
    alignItems: "stretch",
  },
  errorCard: {
    flex: 1,
    alignSelf: "stretch",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(208, 213, 221, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: spacing.sm,
    justifyContent: "center",
    alignItems: "center",
    minWidth: 260,
    maxWidth: 320,
  },
  loadingCard: {
    flex: 1,
    alignSelf: "stretch",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(208, 213, 221, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: spacing.xl,
    justifyContent: "center",
    alignItems: "center",
    minWidth: 260,
    maxWidth: 320,
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: 16,
  },
  retryButton: {
    minWidth: 120,
  },
  onboardingPromptCard: {
    backgroundColor: colors.blue[50],
    borderRadius: radii.xl,
    padding: spacing.lg,
    marginBottom: spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: colors.blue[200],
    width: "100%",
    maxWidth: 768,
    alignSelf: "center",
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 24,
    },
    shadowOpacity: 0.18,
    shadowRadius: 48,
    overflow: "hidden",
    // @ts-expect-error - web-only blur
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  onboardingPromptContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: spacing.md,
  },
  onboardingPromptIcon: {
    width: 40,
    height: 40,
    borderRadius: radii.full,
    backgroundColor: colors.blue[100],
    alignItems: "center",
    justifyContent: "center",
  },
  onboardingPromptTitle: {
    color: colors.blue[900],
    fontWeight: "500",
    flex: 1,
  },
  onboardingPromptActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    marginLeft: spacing.md,
  },
  onboardingPromptButton: {
    marginRight: spacing.xs,
  },
  onboardingPromptClose: {
    padding: spacing.xs,
    ...(Platform.OS === "web" && { cursor: "pointer", outlineStyle: "none" }),
  },
});

export default CompanionHomeView;
